import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api/client'

const AuthContext = createContext(null)

// Decode JWT payload without verification (for reading expiration)
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// How many minutes before expiration to trigger refresh
const REFRESH_BUFFER_MINUTES = 2
// How many minutes of inactivity before we stop refreshing tokens
const INACTIVITY_TIMEOUT_MINUTES = 30

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const refreshTimeoutRef = useRef(null)
  const lastActivityRef = useRef(Date.now())

  // Refresh the access token
  const refreshToken = useCallback(async () => {
    const currentToken = localStorage.getItem('auth_token')
    if (!currentToken) return false

    try {
      const response = await api.post('/api/v1/auth/refresh')
      if (response.ok) {
        const data = await response.json()
        setToken(data.access_token)
        setUser(data.user)
        localStorage.setItem('auth_token', data.access_token)
        localStorage.setItem('user', JSON.stringify(data.user))
        console.log('Token refreshed successfully')
        return true
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
    }
    return false
  }, [])

  // Check if user has been active recently
  const isUserActive = useCallback(() => {
    const inactivityLimit = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000
    return Date.now() - lastActivityRef.current < inactivityLimit
  }, [])

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
  }, [])

  // Schedule token refresh before expiration
  const scheduleTokenRefresh = useCallback((authToken) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    const payload = decodeJwtPayload(authToken)
    if (!payload?.exp) return

    const expiresAt = payload.exp * 1000 // Convert to milliseconds
    const now = Date.now()
    const refreshAt = expiresAt - (REFRESH_BUFFER_MINUTES * 60 * 1000)
    const delay = refreshAt - now

    if (delay > 0) {
      console.log(`Token refresh scheduled in ${Math.round(delay / 1000 / 60)} minutes`)
      refreshTimeoutRef.current = setTimeout(async () => {
        // Only refresh if user has been active
        if (!isUserActive()) {
          console.log('User inactive, skipping token refresh. Token will expire naturally.')
          return
        }
        const success = await refreshToken()
        if (success) {
          // Schedule next refresh with new token
          const newToken = localStorage.getItem('auth_token')
          if (newToken) scheduleTokenRefresh(newToken)
        }
      }, delay)
    } else if (isUserActive()) {
      // Token is about to expire or already expired, try refresh immediately if active
      refreshToken()
    }
  }, [refreshToken, isUserActive])

  // Check for stored auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(JSON.parse(storedUser))
      // Schedule refresh for existing token
      scheduleTokenRefresh(storedToken)
    }
    setLoading(false)
  }, [scheduleTokenRefresh])

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']

    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity)
      })
    }
  }, [updateActivity])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  // Register 401 handler to auto-logout on expired tokens
  useEffect(() => {
    api.setOnUnauthorized(() => {
      console.log('Session expired, logging out...')
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      setToken(null)
      setUser(null)
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
    })
  }, [])

  const login = useCallback((authToken, userData) => {
    setToken(authToken)
    setUser(userData)
    localStorage.setItem('auth_token', authToken)
    localStorage.setItem('user', JSON.stringify(userData))
    // Schedule token refresh
    scheduleTokenRefresh(authToken)
  }, [scheduleTokenRefresh])

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  }

  const updateUser = (userData) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('auth_token')

    if (!storedToken) {
      return
    }

    try {
      const response = await api.get('/api/v1/me')

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
        localStorage.setItem('user', JSON.stringify(userData))
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error)
    }
  }

  // Helper function to check if user needs profile completion
  const needsProfileCompletion = () => {
    // User needs profile completion if they don't have both first and last name
    return !user?.first_name || !user?.last_name
  }

  // Helper function to check if user needs onboarding
  const needsOnboarding = () => {
    return !user?.has_completed_onboarding || !user?.active_tenancy_id
  }

  // Get the active tenancy object
  const getActiveTenancy = () => {
    if (!user?.active_tenancy_id || !user?.tenancies) return null
    return user.tenancies.find(t => t.id === user.active_tenancy_id) || null
  }

  // Get the active tenancy slug for URL routing
  const getActiveSlug = () => {
    const tenancy = getActiveTenancy()
    return tenancy?.slug || null
  }

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    needsProfileCompletion,
    needsOnboarding,
    getActiveTenancy,
    getActiveSlug,
    login,
    logout,
    updateUser,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
